import { Component, computed, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { Group } from '../../models/group-services.model';
import { CategoryService } from '../../models/category-services.model';
import { Service } from '../../models/services.model';
import { ServicesService } from '../../services/services.service';
import { BranchesService } from '../../services/branches.service';
import { ComprehensiveProgramsService } from '../../services/comprehensive-programs.service';

interface ColumnLink { label: string; url: string; external?: boolean; heading?: boolean; option?: boolean }
interface Column { title: string; url?: string; items: ColumnLink[] }
interface RailItem { key: string; label: string }
interface GroupsWithCategories {
  groups: Group[];
  categories: Record<string, CategoryService[]>;
  services: Record<string, Service[]>;
}

const STATIC_CONTENT: Record<string, Column[]> = {
  'compra-un-mack': [
    {
      title: 'Financiamiento',
      items: [{ label: 'Mack Financial Services', url: '/mack-financial-services' }],
    },
    {
      title: 'Seminuevos',
      items: [{ label: 'Camiones', url: 'https://tienda.camionesmack.mx/collections/seminuevos', external: true }],
    },
  ],
};

@Component({
  selector: 'app-megamenu-soluciones',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './megamenu-soluciones.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './megamenu-soluciones.scss',
})
export class MegamenuSolucionesComponent {
  private readonly servicesService = inject(ServicesService);
  private readonly branchesService = inject(BranchesService);
  private readonly comprehensiveProgramsService = inject(ComprehensiveProgramsService);

  active = signal<string>('compra-un-mack');
  linkClick = output<void>();

  private readonly branches = toSignal(this.branchesService.getBranches(), { initialValue: [] });

  private readonly programs = toSignal(this.comprehensiveProgramsService.getComprehensivePrograms(), {
    initialValue: [],
  });

  private readonly data = toSignal<GroupsWithCategories, GroupsWithCategories>(
    this.servicesService.getServiceGroupCategories().pipe(
      switchMap((groups) => {
        if (!groups.length) return of<GroupsWithCategories>({ groups, categories: {}, services: {} });
        return forkJoin(
          Object.fromEntries(
            groups.map((g) => [String(g.id), this.servicesService.getServiceCategories(g.id)])
          ) as Record<string, ReturnType<ServicesService['getServiceCategories']>>
        ).pipe(
          switchMap((categories) => {
            const allCats = Object.values(categories).flat();
            if (!allCats.length) return of<GroupsWithCategories>({ groups, categories, services: {} });
            return forkJoin(
              Object.fromEntries(
                allCats.map((c) => [String(c.id), this.servicesService.getServices(c.id)])
              ) as Record<string, ReturnType<ServicesService['getServices']>>
            ).pipe(map((services) => ({ groups, categories, services } as GroupsWithCategories)));
          })
        );
      })
    ),
    { initialValue: { groups: [], categories: {}, services: {} } as GroupsWithCategories }
  );

  readonly rail = computed<RailItem[]>(() => {
    const sorted = [...this.data().groups].sort((a, b) => Number(a.order) - Number(b.order));
    return [
      { key: 'compra-un-mack', label: 'Compra un mack' },
      ...sorted.map((g) => ({ key: String(g.id), label: g.title })),
      { key: 'sucursales', label: 'Sucursales' },
    ];
  });

  readonly content = computed<Record<string, Column[]>>(() => {
    const { groups, categories, services } = this.data();
    const sorted = [...groups].sort((a, b) => Number(a.order) - Number(b.order));
    const dynamic: Record<string, Column[]> = {};
    for (const g of sorted) {
      const cats = categories[String(g.id)] ?? [];
      dynamic[String(g.id)] = cats.length
        ? cats.map((c: CategoryService) => ({
            title: c.title,
            url: `/servicio/${c.slug}`,
            items: (services[String(c.id)] ?? []).map((s: Service) => ({
              label: s.title,
              url: `/servicio/${c.slug}/${s.slug}`,
            })),
          }))
        : [];
    }

    const branchLinks = this.branches().map((b) => ({ label: b.name, url: `/sucursales/${b.slug}` }));
    const verTodas = { label: 'Ver todas nuestras sucursales', url: '/sucursales' };
    const chunkSize = 3;
    const sucursalesColumns: Column[] = [];
    for (let i = 0; i < branchLinks.length; i += chunkSize) {
      sucursalesColumns.push({ title: '', items: branchLinks.slice(i, i + chunkSize) });
    }
    if (sucursalesColumns.length) {
      sucursalesColumns[sucursalesColumns.length - 1].items.push(verTodas);
    } else {
      sucursalesColumns.push({ title: '', items: [verTodas] });
    }

    const programasColumn: Column = {
      title: 'Programas integrales',
      items: [
        ...this.programs().map((p) => ({ label: p.title, url: `/programas-integrales/${p.slug}` })),
        { label: 'Ver Programas integrales', url: '/programas-integrales' },
      ],
    };

    return {
      ...STATIC_CONTENT,
      ...dynamic,
      'compra-un-mack': [programasColumn, ...STATIC_CONTENT['compra-un-mack']],
      sucursales: sucursalesColumns,
    };
  });

  setActive(key: string) {
    this.active.set(key);
  }

  onLinkClick() {
    this.linkClick.emit();
  }
}
